import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useIntl } from "react-intl";
import SnackBarTool from "../../../../_metronic/_partials/widgets/snack-bar/SnackBar";
import { LoadingCustome } from "../../../../_metronic/_partials/controls/LoadingCustome";
import { getApiErrorMessage } from "../../../utils/commonFunction";
import {
  deleteLockoutGrace,
  getLockoutGrace,
  updateLockoutGrace,
} from "../_redux/order/orderCrud";
import {
  buildLockoutGracePayload,
  hasManualLockoutGraceOverride,
  isLockoutGraceNotFound,
  parseLockoutGraceDays,
} from "../lockoutGraceUtils";

/**
 * Modal for adding, updating, or removing a container order lockout grace period.
 *
 * @param {object} props - Component props.
 * @param {{ check: boolean }} props.dialog - Dialog open state.
 * @param {Function} props.setDialog - Setter for dialog state.
 * @param {number|string} props.orderId - Order id.
 * @param {Function} [props.onSuccess] - Callback after a successful save or delete.
 * @returns {JSX.Element|null} The grace period modal.
 */
const GracePeriodModal = ({
  dialog,
  setDialog,
  orderId,
  onSuccess,
}) => {
  const intl = useIntl();
  const closeTimerRef = useRef(null);
  const [days, setDays] = useState("");
  const [hasManualOverride, setHasManualOverride] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [snack, setSnack] = useState({ call: false, type: "", message: "" });

  const isBusy = fetching || submitting;

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!orderId) {
      return undefined;
    }

    let cancelled = false;

    getLockoutGrace(orderId)
      .then((res) => {
        if (cancelled) {
          return;
        }

        const currentDays = parseLockoutGraceDays(res);
        setHasManualOverride(hasManualLockoutGraceOverride(res));
        setDays(currentDays != null ? String(currentDays) : "");
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }

        if (isLockoutGraceNotFound(error)) {
          setHasManualOverride(false);
          setDays("");
          return;
        }

        setSnack({
          call: true,
          type: "error",
          message: getApiErrorMessage(error),
        });
      })
      .finally(() => {
        if (!cancelled) {
          setFetching(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [orderId]);

  /**
   * Close the modal after a short success delay.
   *
   * @returns {void}
   */
  const scheduleClose = () => {
    closeTimerRef.current = setTimeout(() => {
      setDialog({ check: false });
    }, 1500);
  };

  /**
   * Persist the entered grace period for the order.
   *
   * @returns {void}
   */
  const handleSave = () => {
    if (!orderId) {
      return;
    }

    const trimmedDays = String(days).trim();

    if (trimmedDays === "") {
      setSnack({
        call: true,
        type: "error",
        message: "Please enter the number of grace days.",
      });
      return;
    }

    const parsedDays = Number(trimmedDays);

    if (!Number.isInteger(parsedDays) || parsedDays < 0) {
      setSnack({
        call: true,
        type: "error",
        message: "Please enter a valid number of grace days.",
      });
      return;
    }

    setSubmitting(true);
    setSnack({ call: false, type: "", message: "" });

    updateLockoutGrace(orderId, buildLockoutGracePayload(parsedDays))
      .then((res) => {
        setHasManualOverride(true);
        setDays(String(parsedDays));
        setSnack({
          call: true,
          type: "success",
          message: res?.data?.message || "Grace period saved.",
        });
        onSuccess?.();
        scheduleClose();
      })
      .catch((error) => {
        setSnack({
          call: true,
          type: "error",
          message: getApiErrorMessage(error),
        });
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  /**
   * Remove the manual grace period override from the order.
   *
   * @returns {void}
   */
  const handleDelete = () => {
    if (!orderId) {
      return;
    }

    setSubmitting(true);
    setSnack({ call: false, type: "", message: "" });

    deleteLockoutGrace(orderId)
      .then((res) => {
        setHasManualOverride(false);
        setDays("");
        setSnack({
          call: true,
          type: "success",
          message: res?.data?.message || "Grace period removed.",
        });
        onSuccess?.();
        scheduleClose();
      })
      .catch((error) => {
        setSnack({
          call: true,
          type: "error",
          message: getApiErrorMessage(error),
        });
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  if (!dialog?.check) {
    return null;
  }

  return (
    <Modal
      show={dialog.check}
      onHide={() => !isBusy && setDialog({ check: false })}
    >
      <Modal.Header closeButton>
        <Modal.Title>Grace Period</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {fetching ? (
          <p className="text-muted mb-0">Loading grace period...</p>
        ) : (
          <>
            <label className="form-label" htmlFor="grace-period-days">
              Number of grace days
            </label>
            <input
              id="grace-period-days"
              type="number"
              min="0"
              step="1"
              className="form-control"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              placeholder="Enter number of days"
              disabled={isBusy}
            />
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {hasManualOverride && !fetching && (
          <button
            type="button"
            className="btn btn-delete btn-elevate mr-auto"
            onClick={handleDelete}
            disabled={isBusy}
          >
            Remove
          </button>
        )}
        <button
          type="button"
          className="btn btn-light btn-elevate"
          onClick={() => setDialog({ check: false })}
          disabled={isBusy}
        >
          {intl.formatMessage({ id: "Cancel" })}
        </button>
        <button
          type="button"
          className="btn btn-primary btn-elevate"
          onClick={handleSave}
          disabled={isBusy || fetching}
        >
          Save
        </button>
      </Modal.Footer>
      {submitting && <LoadingCustome />}
      {snack.call ? <SnackBarTool {...snack} /> : ""}
    </Modal>
  );
};

export default GracePeriodModal;
